import type { ListQuotesParams, QuoteListItem } from "@/api/quotes";
import {
  knownQuoteStatus,
  quotePrefillLabel,
  quoteStatusLabel,
} from "@/components/quote/quoteVocabulary";
import { formatFactoryDate } from "@/utils/factoryTime";

/** 既有本地工具的兼容导出；F24服务端列表不再调用，避免丢仅图号命中的行。 */
export function filterQuoteList(
  items: QuoteListItem[],
  status: string,
  query: string,
) {
  const needle = query.trim().toLocaleLowerCase();
  return items.filter(
    (item) =>
      (status === "all" ||
        (status === "needs_action"
          ? ["draft", "rejected"].includes(item.status)
          : item.status === status)) &&
      (!needle ||
        [item.title, item.customer_name].some((value) =>
          value?.toLocaleLowerCase().includes(needle),
        )),
  );
}
/** 每页条数，搜索覆盖全库，不把此值当搜索范围或总数。 */
export const QUOTE_LIST_LIMIT = 100;

/**
 * 一个全局分页请求。待处理别名由后台统一展开为草稿+驳回，不能各翻两页再合并。
 */
export function quoteListRequests(
  status: string,
  filters: {
    query?: string;
    offset?: number;
    dateFrom?: string;
    dateTo?: string;
  } = {},
): ListQuotesParams[] {
  return [
    {
      ...(status === "all" ? {} : { status }),
      limit: QUOTE_LIST_LIMIT,
      offset: filters.offset ?? 0,
      exclude_project_lines: true,
      ...(filters.query?.trim() ? { q: filters.query.trim() } : {}),
      ...(filters.dateFrom ? { date_from: filters.dateFrom } : {}),
      ...(filters.dateTo ? { date_to: filters.dateTo } : {}),
    },
  ];
}

/**
 * 「待审批·N」的单独计数(续做裁定 §9):每次刷新列表另取一次待审单,各分段读数一致,也数得到最近 100 张以外的。
 * 主请求是没有q/日期的待审批时复用total；否则独立读全单件待审总数。
 */
export function pendingReviewCountRequest(
  status: string,
  filters: { query?: string; dateFrom?: string; dateTo?: string } = {},
): ListQuotesParams | null {
  return status === "pending_review" &&
    !filters.query?.trim() &&
    !filters.dateFrom &&
    !filters.dateTo
    ? null
    : {
        status: "pending_review",
        limit: 1,
        offset: 0,
        exclude_project_lines: true,
      };
}

/** 只显示服务端真实总数；未知或0不写数字。 */
export function pendingReviewBadge(count: number | null): string {
  if (count == null || !Number.isSafeInteger(count) || count <= 0)
    return "待审批";
  return `待审批·${count}`;
}

export function quoteListDateValid(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return (
    Number.isFinite(date.getTime()) &&
    date.toISOString().slice(0, 10) === value &&
    value < "9999-01-01"
  );
}
export function quoteListQueryError(
  query: string,
  dateFrom: string,
  dateTo: string,
): string | null {
  if (query.length > 100) return "搜索最多100个字符；已保留原文，请缩短后查询";
  if (query.trim() && query.normalize("NFKC").replace(/\s/g, "").length < 2)
    return "请至少输入2个字符搜索名称、客户或图号";
  if (
    (dateFrom && !quoteListDateValid(dateFrom)) ||
    (dateTo && !quoteListDateValid(dateTo))
  )
    return "请输入有效的建单日期";
  if (dateFrom && dateTo && dateFrom > dateTo)
    return "建单开始日期不能晚于结束日期";
  return null;
}

export function quoteListTotal(value: unknown): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? value
    : null;
}

/**
 * 既有本地合并工具的兼容导出，F24全局分页不使用。多份按时间倒序合并、按 id 去重。不再截前 100(复审 2026-10-25):
 * 「待处理」两份各最多 100 张,截掉的正是服务端已经返回、却比 100 张新草稿旧的驳回单(要返工的单看不到)。
 */
export function mergeQuoteLists(lists: QuoteListItem[][]): QuoteListItem[] {
  if (lists.length === 1) return lists[0];
  const seen = new Set<string>();
  return lists
    .flat()
    .filter((item) => !seen.has(item.id) && !!seen.add(item.id))
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
}

export function quoteListState(item: QuoteListItem) {
  return quotePrefillLabel(item.prefill_state) ?? quoteStatusLabel(item.status);
}
/**
 * 已批准报价的有效期还剩几天以内算「快过期」。总纲没给数：默认报价有效 30 天
 * （quote_policy DEFAULTS.validity.days），取一周——够跟进客户，又不会新单当天就标。
 */
export const QUOTE_EXPIRING_SOON_DAYS = 7;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
function utcDay(iso: string): number | null {
  const match = ISO_DATE.exec(iso);
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number);
  const value = Date.UTC(year, month - 1, day);
  const back = new Date(value);
  return back.getUTCFullYear() === year &&
    back.getUTCMonth() === month - 1 &&
    back.getUTCDate() === day
    ? value / 86_400_000
    : null;
}
/** 工厂当天（北京时间）的 YYYY-MM-DD，不按浏览器时区取日。 */
function factoryIsoDate(now: Date): string {
  return formatFactoryDate(now).replaceAll("/", "-");
}
export type QuoteListValidity = { label: "快过期" | "已过期"; detail: string };
/**
 * 只看已返回的 valid_until：已批准的有效期是审批时冻结的；草稿 / 待审的是按今天预览、每次读都顺延，不标。
 * 成交 / 丢单结果要 F24 才有，已成交的已批准单这里也会标。
 */
export function quoteListValidity(
  item: QuoteListItem,
  now: Date,
): QuoteListValidity | null {
  if (item.status !== "approved" || !item.valid_until) return null;
  const until = utcDay(item.valid_until),
    today = utcDay(factoryIsoDate(now));
  if (until === null || today === null) return null;
  const left = until - today;
  const prefix = `有效期至 ${item.valid_until}`;
  if (left < 0) return { label: "已过期", detail: `${prefix}，已过期` };
  if (left > QUOTE_EXPIRING_SOON_DAYS) return null;
  return {
    label: "快过期",
    detail: `${prefix}，${left === 0 ? "今天到期" : `还剩 ${left} 天`}`,
  };
}
export function quoteListHasPrice(item: QuoteListItem) {
  return (
    !quotePrefillLabel(item.prefill_state) && knownQuoteStatus(item.status)
  );
}

/** Only public protocol fields enter this summary; never interpolate Error.message or response data. */
export function quoteListSearchMessage(protocol?: {
  status: number;
  code: string | null;
}): string | null {
  if (protocol?.status !== 422) return null;
  if (protocol.code === "QUOTE_SEARCH_TOO_BROAD")
    return "图号太短或条件太宽,请多输几位,或加客户、日期";
  if (protocol.code === "QUOTE_SEARCH_TERM_TOO_SHORT")
    return "搜索词至少 2 个字";
  return null;
}
export function quoteListReadFailure(protocol?: {
  status: number;
  code: string | null;
}) {
  const status =
    protocol &&
    Number.isInteger(protocol.status) &&
    (protocol.status === 0 ||
      (protocol.status >= 100 && protocol.status <= 599))
      ? protocol.status
      : null;
  const code =
    protocol?.code && /^[A-Z][A-Z0-9_]{1,63}$/.test(protocol.code)
      ? protocol.code
      : "UNKNOWN_ERROR";
  const message =
    quoteListSearchMessage(protocol) ??
    (status === 401
      ? "登录状态已失效，请重新登录"
      : status === 403
        ? "当前账号不能读取报价列表"
        : status === 0
          ? "网络未连接，请稍后重试"
          : status !== null && status >= 500
            ? "服务暂时不可用，请稍后重试"
            : "报价列表暂时无法读取，请重试");
  return `GET /v1/quotes?limit=100\nHTTP：${status ?? "未知"}\n错误码：${code}\n${message}\n已打开的报价不受影响`;
}
